import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ProjectLayout } from '@/components/layout/ProjectLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useProject, useDeleteProject } from '@/hooks/useProjects'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useProjectClips,
  useProjectVfxShotsStats,
  useProjectActivity,
  useProjectCommits,
  useProjectSequences,
  useProjectAssignedClips,
  useProjectAssignedVfxShots,
  formatRelativeTime
} from '@/hooks/useDashboardStats'
import { Film, GitCommit, Layers, Activity as ActivityIcon, Monitor, UserCircle, FileVideo, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { ActivityFeed } from '@/components/activity/ActivityFeed'
import { useAnalytics } from '@/hooks/useAnalytics'

export function ProjectDashboardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: project, isLoading, error } = useProject(id)
  const deleteProject = useDeleteProject()
  const permissions = usePermissions(id, project?.owner_user_id)
  const { trackProjectDeleted } = useAnalytics()

  // Project-specific dashboard statistics
  const { data: clipCount, isLoading: clipsLoading } = useProjectClips(id)
  const { data: vfxStats, isLoading: vfxLoading } = useProjectVfxShotsStats(id)
  const { data: activityData, isLoading: activityLoading } = useProjectActivity(id)
  const { data: commitsData, isLoading: commitsLoading } = useProjectCommits(id)
  const { data: sequenceCount, isLoading: sequencesLoading } = useProjectSequences(id)

  // Project-specific assignments (items assigned to current user in THIS project)
  // Note: For global cross-project assignments, see AssignedToMeSection.tsx
  const { data: assignedClips } = useProjectAssignedClips(id)
  const { data: assignedVfxShots } = useProjectAssignedVfxShots(id)

  // Confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!project) return

    try {
      await deleteProject.mutateAsync(project.id)

      // Track project deletion (project_id only - no project_name for privacy)
      trackProjectDeleted({
        project_id: project.id,
      })

      toast.success('Project deleted successfully')
      navigate('/projects')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete project')
    }
  }

  const handleModalClose = (open: boolean) => {
    setDeleteConfirmOpen(open)
    if (!open) {
      setConfirmText('')
    }
  }

  const isConfirmTextValid = project && confirmText === project.name

  if (isLoading) {
    return (
      <ProjectLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading project...</p>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  if (error || !project) {
    return (
      <ProjectLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-destructive mb-4">Failed to load project</p>
            <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
          </div>
        </div>
      </ProjectLayout>
    )
  }

  return (
    <ProjectLayout projectName={project.name}>
      <div className="space-y-6">
        {/* Delete Confirmation Modal */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={handleModalClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Project
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this project?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This will permanently delete <span className="font-bold text-foreground">{project.name}</span> and all
                associated data including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>All sequences and commits</li>
                <li>All clips and VFX shots</li>
                <li>All comments and activity logs</li>
                <li>All team members and invitations</li>
              </ul>
              <p className="text-destructive font-semibold text-sm">This action cannot be undone.</p>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  To confirm deletion, please type the exact project name:{' '}
                  <span className="font-mono font-bold text-foreground">{project.name}</span>
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Enter project name"
                  className="font-mono"
                  autoComplete="off"
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteProject.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={!isConfirmTextValid || deleteProject.isPending}
                className="bg-destructive hover:bg-destructive/90"
              >
                {deleteProject.isPending ? 'Deleting...' : 'Delete Project'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">{project.name}</h2>
            <p className="text-muted-foreground">
              {project.description || 'No description'}
            </p>
          </div>
          {permissions.canDeleteProject && (
            <Button variant="destructive" onClick={handleDeleteClick} disabled={deleteProject.isPending}>
              Delete Project
            </Button>
          )}
        </div>

        {/* Dashboard Metrics - 3x2 Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clips</p>
                <p className="text-3xl font-semibold mt-2">
                  {clipsLoading ? '...' : clipCount || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Film className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sequences</p>
                <p className="text-3xl font-semibold mt-2">
                  {sequencesLoading ? '...' : sequenceCount || 0}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Monitor className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Timeline Commits</p>
                <p className="text-3xl font-semibold mt-2">
                  {commitsLoading ? '...' : commitsData?.count || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <GitCommit className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">VFX Shots In Progress</p>
                <p className="text-3xl font-semibold mt-2">
                  {vfxLoading ? '...' : vfxStats?.inProgress || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Currently active</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Layers className="h-6 w-6 text-orange-500" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total VFX Shots</p>
                <p className="text-3xl font-semibold mt-2">
                  {vfxLoading ? '...' : vfxStats?.total || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {vfxLoading ? '...' : `${vfxStats?.approved || 0} approved`}
                </p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Layers className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recent Activity</p>
                <p className="text-3xl font-semibold mt-2">
                  {activityLoading ? '...' : activityData?.count || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Actions (30 days)</p>
              </div>
              <div className="h-12 w-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <ActivityIcon className="h-6 w-6 text-indigo-500" />
              </div>
            </div>
          </Card>
        </div>

        {/* Assigned to Me and Activity Side-by-Side */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Assigned to Me Section - Left Column */}
          {((assignedClips && assignedClips.length > 0) || (assignedVfxShots && assignedVfxShots.length > 0)) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCircle className="h-5 w-5" />
                  Assigned to Me
                </CardTitle>
                <CardDescription>Items assigned to you in this project</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignedClips?.map((clip) => (
                    <div
                      key={clip.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => navigate(`/projects/${id}/clips?clip=${clip.id}`)}
                    >
                      <div className="h-10 w-10 rounded bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                        <FileVideo className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{clip.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Clip • {clip.assigned_at ? `Assigned ${formatRelativeTime(clip.assigned_at)}` : 'Recently assigned'}
                        </p>
                      </div>
                    </div>
                  ))}
                  {assignedVfxShots?.map((shot) => (
                    <div
                      key={shot.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => navigate(`/projects/${id}/vfx?shot=${shot.id}`)}
                    >
                      <div className="h-10 w-10 rounded bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        <Film className="h-5 w-5 text-purple-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{shot.shot_name}</p>
                        <p className="text-xs text-muted-foreground">
                          VFX Shot • {shot.frame_range_start}-{shot.frame_range_end}f
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {shot.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Activity and Timeline Commits - Right Column (Stacked) */}
          <div className="space-y-6">
            {/* Activity Section */}
            <ActivityFeed projectId={id} showHeader={true} />

            {/* Timeline Commits */}
            <Card>
              <CardHeader>
                <CardTitle>Timeline Commits</CardTitle>
                <CardDescription>Recent timeline version commits</CardDescription>
              </CardHeader>
              <CardContent>
                {commitsLoading ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Loading commits...</p>
                ) : !commitsData?.commits || commitsData.commits.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No recent commits</p>
                ) : (
                  <div className="space-y-4">
                    {commitsData.commits.map((commit: any, index: number) => (
                      <div key={commit.id || index} className="flex items-start gap-3 py-2 border-b last:border-b-0">
                        <div className="mt-0.5 text-blue-500">
                          <GitCommit className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{commit.message || 'Timeline update'}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {commit.sequence_name && <span className="font-medium">{commit.sequence_name}</span>}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {commit.author_name || 'Unknown'} • {formatRelativeTime(commit.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

      </div>
    </ProjectLayout>
  )
}
